import React, { useEffect, useRef, useState } from 'react';
import delay from 'lodash/delay';

import {AddCartStatus, BuilderReturn} from '../types/builder';

import useModelData from './data/useModelData';
import useImageFiles from './data/useImageFiles';
import useTeamMembers from './data/useTeamMembers';
import { Observable } from '../utils/Observable';

type BuilderControllerLite = {
  builderLayerComposer: any;
  builderInteraction: any
  events: Observable;
  destroy: Function,
  setCamera: Function,
  rotateCamera:Function,
  swipeCamera:Function,
  onLayersUpdate: Function,
  getLayers: Function,
  setLayerColor: Function,
  updateText: Function,
  updateModel: Function,
  updateModelMouseMat: Function,
  cameraList: { name: string }[],
  updateCameras:Function,
  activeLayer:Function,
  hasRecentLayerInteraction: Function,
  setUiContext: Function,
};

export default function useBuilder(canvas, manifestURL, shareData): BuilderReturn {
  const controller = useRef<BuilderControllerLite>();
  const [BuilderControllerModule, setBuilderControllerModule] = useState<any>();

  const [cameraList, setCameraList] = useState<{name:string}[]>();
  const [layersData, setLayersData] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);

  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [options, setOptions] = useState(null);
  const [manifest, setManifest] = useState({});
  const [logoTabs, setLogoTabs] = useState({});
  const [animationState, setAnimationState] = useState(false);
  const [saveBuilderState, setSaveBuilderState] = useState(false);
  const [updateTimeoutId, setUpdateTimeoutId] = useState(null);
  const [isFirstShCompMessage, setFirstShCompMessage] = useState(false);
  const [isShowCompMessage, setIsShowCompMessage] = useState( false);
  const [isMobShowCompMessage, setIsMobShowCompMessage] = useState(false);
  const [addCart, setAddCart] = useState<AddCartStatus>(false);
  const [cnCart, setCnCart ] = useState(0);
  const [curentSizeMouseMat, setCurrentSizeMouseMat] = useState('XL');
  const [curentUserid, setCurentUserid] = useState(0);

  const modelData = useModelData(controller, shareData?.modelData, options, isReady);
  const imagesData = useImageFiles(controller, shareData, options, isReady, logoTabs, curentSizeMouseMat);
  const membersData = useTeamMembers(controller, shareData?.membersData, options, isReady);

  import('../BuilderController')
    .then(builder => setBuilderControllerModule(builder))

  useEffect(() => {
     membersData.updateMemberPreview();
    if(process.env.REACT_APP_ENV === "production") {
      let us = window['getCarentUsId']();
      setCurentUserid(us);
    }
    //update all members after change colors, patterns, logo
    //after logo change builder is broken
    //updateMembersPreview();
  },[isReady, layersData, modelData.colorList, imagesData.sponsorImages, imagesData.teamImage, imagesData.image, membersData.currentMember.preview])

  useEffect(() => {
    if (BuilderControllerModule && canvas && manifestURL) {
      const builder = new BuilderControllerModule.default(canvas.current, manifestURL);
      builder.events.on('load_composer', (layers) => {
        setLayersData(layers);
      });
      builder.events.on('progress', prc => setProgress(prc));
      builder.events.on('load_model', manifest => {
        // Populate model data from manifest
        membersData.setPrice(manifest.price || '');
        membersData.setColorTheme(manifest.colorTheme || []);
        modelData.setFonts(manifest.fonts || []);
        modelData.setFlags(manifest.flags || []);
        modelData.setPalette(manifest.palette || []);
        modelData.setPatternList(manifest.patterns || []);
        modelData.setCollarList(manifest.collars || []);
        modelData.setSurffaceList(manifest.surface || []);
        setOptions({...manifest.options});
        setCameraList(manifest.camera.reduce((acc, item) => (acc = { ...acc, [item.name]:"" }), {}));
        setManifest(manifest);
        setLogoTabs({...logoTabs, teamLogo:manifest.team_logo, sponsorLogo:manifest.sponsor_logo,image:manifest.image, additionalImage:manifest.additional_images});

        // Setting default values
        modelData.setPattern(shareData?.modelData?.pattern ?? manifest?.defaults?.pattern ?? '');
        modelData.setCollar(shareData?.modelData?.collar ?? manifest?.defaults?.collar ?? '');
        modelData.setSurfface(shareData?.modelData?.surface ?? manifest?.defaults?.surface ?? '');

        updateText(membersData.currentMember);
        clearTimeout(updateTimeoutId);
        setUpdateTimeoutId(setTimeout(() => !animationState && membersData.updateMemberPreview(), 0));
        
        membersData.initMemberFonts(manifest?.fonts.length ? manifest?.fonts[0] : "");


        delay(() => setIsReady(true), 500)
      });
      builder.events.on("set_animation_state", () => setAnimationState(false))

      controller.current = builder;
    }
    return () => controller?.current?.destroy();
  }, [BuilderControllerModule, canvas, manifestURL, controller]);

  // Update text
  useEffect(() => {
    updateText(membersData.currentMember);
    clearTimeout(updateTimeoutId);
    setUpdateTimeoutId(setTimeout(() => !animationState && membersData.updateMemberPreview(), 0));
  }, [controller, membersData.currentMemberText, membersData.currentMember, membersData.memberTextColors, membersData.memberFontFamily]);

  // Update model
  useEffect(() => {
    controller?.current?.updateModel(modelData?.collar, membersData?.currentMember?.data?.sleeve);
    membersData.updateMemberPreview();
    if(membersData.members[0].modelType !== undefined){
      if(membersData.members[0].modelType === "3dMouseMat"){
        controller?.current?.updateModelMouseMat(membersData.members[0].data.size ? membersData.members[0].data.size : 'XL',true);
      }
    }
  }, [controller, modelData?.collar, membersData?.currentMember.data.sleeve, modelData.collarList, modelData.surfface]);

  // Update model mouseMat
  useEffect(() => {
    const newSize = membersData?.currentMember?.data?.size || 'XL';
    setCurrentSizeMouseMat(newSize);
    controller?.current?.updateModelMouseMat(newSize);

    if (membersData.members[0].modelType === "3dMouseMat") {
      const modelSize = membersData.members[0].data.size || 'XL';
      controller?.current?.updateModelMouseMat(modelSize, true);
    }

    controller?.current?.builderInteraction?.updateSizeMouse3d(membersData?.currentMember?.data?.size);
  }, [membersData?.currentMember?.data?.size]);

  const updateText = (member) => {
    Object.keys(member.text)
      .forEach(textId => controller?.current?.updateText(
        textId,
        { ...member.text[textId], color: membersData.memberTextColors[textId], family: membersData.memberFontFamily },
        (textId, fontSize) => membersData.updateMemberFontSize(textId, fontSize)
      ));
  }

  const updateAddCart = (status) => {
    setAddCart( (prevState):boolean => {
      const state = status;
      return state;
    })
  }


  const setCamera = (cameraName, animation, xDir) => {
    if (isReady) {
      controller?.current?.setCamera(cameraName, animation, xDir);
      setCurrentCameraIndex(Object.keys(cameraList).findIndex(el => el === cameraName));
    }
  };

  const getActiveLayer = () => {
      return layersData.find(({layer}) => layer.active)
  }

  const firstUpdateShCompMessage = (bl) => {
    setFirstShCompMessage(bl);
  }
  const updateShowCompMessage = (bool) => {
    setIsShowCompMessage(bool);
  }
  const updateMobShowCompMessage = (bool) => {
    setIsMobShowCompMessage(bool);
  }
  const upCustCoun = (ctCount) => {
    setCnCart((prevcnCart):number => {
      prevcnCart = ctCount;
      return prevcnCart;
    });
  }

  const rotateCamera = () =>  controller?.current?.rotateCamera();

  const getMembersPreview = async (
    sides = ["back", "front", "left", "right"],
    viewport = { width: 605, height: 725 },
    quality = 0.92
  ) => {
    // Render each member's previews SEQUENTIALLY to avoid WebGL state conflicts.
    // Promise.all(map(async...)) ran all executors in one synchronous tick, which could
    // cause the second member's render to capture stale GPU state from the first member.
    const results: any[] = [];
    for (let i = 0; i < membersData.members.length; i++) {
      const member = membersData.members[i];
      updateText(member);
      controller?.current?.updateModel(modelData?.collar, member?.data?.sleeve);
      // Yield one frame to allow WebGL pipeline to flush before capturing
      await new Promise(r => setTimeout(r, 16));
      const preview = await new Promise<any>((resolve) => {
        controller?.current?.updateCameras(viewport, sides, resolve, quality);
      });
      results.push(preview);
    }

    // Restore current member's state on the 3D model
    const currentIdx = membersData.currentMemberIndex;
    controller?.current?.updateModel(modelData?.collar, membersData.members[currentIdx]?.data?.sleeve);
    updateText(membersData.members[currentIdx]);
    return results;
  }

  // Quick preview: only renders current member's views (4 renders instead of N×4)
  const getQuickPreview = async () => {
    return new Promise<any[]>((resolve) => {
      const currentIdx = membersData.currentMemberIndex;
      controller?.current?.updateCameras(
        { width: 605, height: 725 },
        ["back", "front", "left", "right"],
        (data) => {
          const result = membersData.members.map((_, i) =>
            i === currentIdx ? data : (membersData.members[i]?.model_preview || { back: "", front: "", left: "", right: "" })
          );
          resolve(result);
        }
      );
    });
  };

  //update all members preview after any changes (now it is not used because builder work very slow)
  const updateMembersPreview = () => {
      membersData.members.map(async(member, index) => {
        updateText(member);
        controller?.current?.updateModel(modelData?.collar, member?.data?.sleeve);
        controller?.current?.updateCameras({width:100, height:125}, ["back"], data => membersData.updateMember({ preview:data?.back},index));
      })
  }

  const setOrdering = (ordering) => {
    controller?.current?.builderLayerComposer.setOrdering(ordering);

    const layers = controller?.current?.builderLayerComposer.getLayers?.() || [];
    controller?.current?.events?.trigger('load_composer', layers);

    refreshLayers();
  };

  const refreshLayers = () => {
    controller.current.builderInteraction.forceRefreshLayers();
  }

  const removeAllImageLayers = () => {
    controller.current.builderLayerComposer.removeAllImageLayers();
    refreshLayers();
  }

  const setUiContext = (ordering) => {
    controller?.current?.setUiContext(ordering);
  }


  return {
    setCamera,
    rotateCamera,
    cameraList,
    currentCameraIndex,
    isReady,
    progress,
    options,
    addCart,
    cnCart,
    curentUserid,
    isShowCompMessage,
    isMobShowCompMessage,
    isFirstShCompMessage,

    manifestURL,
    manifest,
    layersData,
    previewRender: cameraList,
    imagesData,
    modelData,
    membersData,
    logoTabs,
    updateCameras:controller?.current?.updateCameras,
    activeLayer:getActiveLayer,
    hasRecentLayerInteraction: () => controller?.current?.hasRecentLayerInteraction?.() ?? false,

    animationState,
    setAnimationState,
    saveBuilderState,
    setSaveBuilderState,
    updateAddCart,
    upCustCoun,
    updateShowCompMessage,
    updateMobShowCompMessage,
    firstUpdateShCompMessage,
    getMembersPreview,
    getQuickPreview,
    setOrdering,
    refreshLayers,
    removeAllImageLayers,
    setUiContext
  };
}
